import { useEffect } from 'react';
import { Outlet } from 'react-router';

import { useAppStore } from '@/store/app.store';
import InlineMessage from '@ingka/inline-message';
import crossCircleIcon from '@ingka/ssr-icon/paths/cross-circle';
import { translate } from '@repo/phrase';
import { Container } from '@repo/ui/container';
import { CommandMenu } from '@sp-saljapro/command-menu/react';

import { Feature } from '@/components/feature/feature.component';
import { LoadingPage } from '@/components/loading';

import { TELEMETRY_LOGS, logTelemetry } from '@/lib/telemetry.lib';

import { useCoworkerPreferences } from './root-layout.hooks';

const RootLayout = () => {
    const { buCode, isLoading, isError } = useCoworkerPreferences({
        queryOptions: { retry: 10, retryDelay: 2000 },
    });
    const bootstrapResult = useAppStore((state) => state.bootstrapResult);
    const markAppInitLogged = useAppStore((state) => state.markAppInitLogged);

    useEffect(() => {
        if (isLoading || isError || !buCode || !bootstrapResult || useAppStore.getState().appInitLogged) {
            return;
        }

        markAppInitLogged();
        logTelemetry('info', TELEMETRY_LOGS.AppInit, {
            config: {
                buCode,
                configSource: bootstrapResult.marketConfigSource,
                translationSource: bootstrapResult.translationSource,
            },
        });
    }, [bootstrapResult, buCode, isError, isLoading, markAppInitLogged]);

    if (isError) {
        return (
            <Container testId="coworker-preferences-error" padding>
                <InlineMessage
                    title={translate('coworker_preferences_unavailable_title')}
                    body={translate('coworker_preferences_unavailable_body')}
                    variant="negative"
                    ssrIcon={crossCircleIcon}
                />
            </Container>
        );
    }

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            <Feature configPath={['commandMenu.enable']}>
                <CommandMenu
                    title={translate('command_menu_title')}
                    searchPlaceholder={translate('command_menu_search_label')}
                    ariaCloseText={translate('close')}
                    noCommandsMessage={translate('command_menu_no_shortkeys')}
                    noMatchingCommandsMessage={translate('command_menu_no_matching_shortkeys')}
                />
            </Feature>
            <Outlet />
        </>
    );
};

export default RootLayout;
